import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Spin, Empty, Button, Tooltip, Avatar, message, Space } from 'antd';
import {
  TrophyOutlined, StarOutlined, ThunderboltOutlined,
  ShareAltOutlined, UserOutlined, ArrowLeftOutlined, LockOutlined,
  InfoCircleOutlined, DownloadOutlined,
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;

const COLOR_MAP = {
  gold:   { bg: 'linear-gradient(135deg,#facc15 0%,#f59e0b 100%)', text: '#78350f' },
  red:    { bg: 'linear-gradient(135deg,#ef4444 0%,#991b1b 100%)', text: '#fff' },
  green:  { bg: 'linear-gradient(135deg,#25D366 0%,#128C7E 100%)', text: '#fff' },
  blue:   { bg: 'linear-gradient(135deg,#3b82f6 0%,#1e40af 100%)', text: '#fff' },
  purple: { bg: 'linear-gradient(135deg,#a855f7 0%,#6b21a8 100%)', text: '#fff' },
  orange: { bg: 'linear-gradient(135deg,#fb923c 0%,#c2410c 100%)', text: '#fff' },
};

async function fetchBlobUrl(cardUrl) {
  const r = await fetch(cardUrl);
  if (!r.ok) throw new Error('fetch falhou');
  return await r.blob();
}

// Baixar direto o PNG. Usa <a download> em vez de window.open pra pegar
// filename customizado e evitar preview em nova aba.
async function downloadCard(cardUrl, filename) {
  const blob = await fetchBlobUrl(cardUrl);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// Compartilhar via Web Share API. Se nao suportar files, tenta share so
// com texto+URL (browser sugere WhatsApp/telegram/etc). Sem suporte
// nenhum, cai pro download.
async function shareCard(cardUrl, filename, shareTitle, shareText, publicUrl) {
  try {
    if (typeof navigator === 'undefined' || !navigator.share) throw new Error('no_share');
    const blob = await fetchBlobUrl(cardUrl);
    const file = new File([blob], filename, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ title: shareTitle, text: shareText, files: [file] });
      return;
    }
    await navigator.share({ title: shareTitle, text: shareText, url: publicUrl });
  } catch (e) {
    if (e?.name === 'AbortError') return;
    // Fallback final: baixa
    await downloadCard(cardUrl, filename).catch(() => window.open(cardUrl, '_blank'));
  }
}

function AchievementCard({ item, player }) {
  const c = COLOR_MAP[item.color] || COLOR_MAP.green;
  const locked = !!item.locked;
  return (
    <Card
      styles={{ body: { padding: 0 } }}
      style={{
        borderRadius: 16, overflow: 'hidden', border: 'none',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        opacity: locked ? 0.75 : 1,
      }}
    >
      <div style={{
        background: locked ? 'linear-gradient(135deg,#94a3b8 0%,#475569 100%)' : c.bg,
        color: locked ? '#fff' : c.text,
        padding: '18px 20px 22px', position: 'relative', minHeight: 180,
        filter: locked ? 'grayscale(0.3)' : undefined,
      }}>
        {/* Logo Futezap topo direito */}
        <div style={{
          position: 'absolute', top: 12, right: 14,
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(0,0,0,0.15)', padding: '4px 8px', borderRadius: 20,
        }}>
          <img src="/pwa/icon-192.png" alt="" width={16} height={16} style={{ borderRadius: 4 }} />
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>FUTEZAP</span>
        </div>
        {/* Cadeado no topo esquerdo quando locked */}
        {locked && (
          <div style={{
            position: 'absolute', top: 12, left: 14,
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'rgba(0,0,0,0.25)', padding: '3px 8px', borderRadius: 20,
          }}>
            <LockOutlined style={{ fontSize: 10 }} />
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.5 }}>NÃO DESBLOQUEADA</span>
          </div>
        )}
        {/* Jogador */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: locked ? 24 : 0 }}>
          <Avatar size={44} src={player.profile_photo} icon={<UserOutlined />}
            style={{ background: 'rgba(255,255,255,0.25)', border: '2px solid rgba(255,255,255,0.4)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.9, letterSpacing: 0.3, textTransform: 'uppercase' }}>
              {item.label}{' '}
              <Tooltip title={item.hint}>
                <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.7 }} />
              </Tooltip>
            </div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {player.name}
            </div>
          </div>
        </div>
        {/* Valor grande */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 44, fontWeight: 900, letterSpacing: -1, lineHeight: 1 }}>
            {typeof item.value === 'number' && !Number.isInteger(item.value)
              ? item.value.toFixed(2)
              : item.value}
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, opacity: 0.85 }}>{item.unit}</span>
        </div>
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
          {locked ? item.hint : (item.extra || item.hint)}
        </div>
      </div>
      <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'flex-end', background: '#fafafa' }}>
        {locked ? (
          <Tooltip title="Desbloqueie essa conquista pra poder compartilhar">
            <Button size="small" icon={<ShareAltOutlined />} disabled>
              Compartilhar
            </Button>
          </Tooltip>
        ) : (() => {
          const url = `/api/community/players/${player.id}/card/${item.key}.png`;
          const publicUrl = `${window.location.origin}${url}`;
          const filename = `futezap-${item.key}-${player.name?.toLowerCase().replace(/\s+/g,'-')}.png`;
          const shareText = `${item.label}: ${item.value} ${item.unit || ''} · Futezap Comunidade`;
          return (
            <Space size={6}>
              <Button size="small" icon={<DownloadOutlined />}
                onClick={() => downloadCard(url, filename).catch(() => message.error('Erro ao baixar'))}>
                Baixar
              </Button>
              <Button size="small" type="primary" ghost icon={<ShareAltOutlined />}
                onClick={() => shareCard(url, filename, `${item.label} — ${player.name}`, shareText, publicUrl)
                  .catch(() => message.error('Erro ao compartilhar'))}>
                Compartilhar
              </Button>
            </Space>
          );
        })()}
      </div>
    </Card>
  );
}

export default function PlayerAchievements() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/community/players/me/achievements');
        setData(r.data);
      } catch (e) { /* silencioso — Empty aparece */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }
  if (!data) {
    return (
      <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
        <Empty description="Não consegui carregar suas conquistas agora" />
      </div>
    );
  }
  const hasAny = data.achievements.some(a => !a.locked);

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => window.history.back()} />
        <Title level={3} style={{ margin: 0 }}>
          <TrophyOutlined style={{ color: '#faad14', marginRight: 8 }} />
          Minhas Conquistas
        </Title>
      </div>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
        Reconhecimento da galera pelo que você faz em campo. Cards em cinza são conquistas ainda não desbloqueadas — jogue, receba elogios e avaliações pra colorir.
      </Text>

      {!hasAny && (
        <Card size="small" style={{ borderRadius: 12, textAlign: 'center', padding: 20, marginBottom: 12, background: '#fffbeb', border: '1px dashed #fbbf24' }}>
          <ThunderboltOutlined style={{ fontSize: 26, color: '#f59e0b' }} />
          <div style={{ fontWeight: 700, marginTop: 8 }}>Você ainda não tem conquistas desbloqueadas</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Conforme a galera te avalia e elogia, os cards abaixo saem do cinza.
          </Text>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        {data.achievements.map(item => (
          <Col xs={24} sm={12} md={12} key={item.key}>
            <AchievementCard item={item} player={data.player} />
          </Col>
        ))}
      </Row>
    </div>
  );
}
